/* eslint-disable @typescript-eslint/explicit-function-return-type -- action creator return types are fully inferred by typesafe-actions */
import { EventSystemStatus, ICalendlyEvent, IMessage, IOffer, ISummary, RecordedInterviewSystemStatus } from 'api-paths-cas';
import { action } from 'typesafe-actions';
import {
  EducationSponsorship,
  FamilySponsorship,
  IArrivalStatus,
  IDocuments,
  IFinanceDocuments,
  IFollowUpDocuments,
  IRisk,
  IVisa,
  OrganisationSponsorship,
  OtherSponsorship,
  ScholarshipSponsorship,
} from '../../types';
import { IEventType } from '../event_types/types';
import { Status, VisaStatus } from './core/types';
import { EnrolmentStatusActionTypes } from './enrolmentStatus/types';
import { StudentActionTypes } from './types';

export * from './offer/actions';
export * from './calendlyEvents/actions';

/* Checklist section - Personal Info */
export const savePI = (source: string, config: unknown) =>
  action(StudentActionTypes.SAVE_PERSONAL_INFO, { source, config });
export const savePISuccess = (cas: IVisa) => action(StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS, cas);
export const savePIFailure = (error: string) => action(StudentActionTypes.SAVE_PERSONAL_INFO_FAILURE, error);
export const updateStudentDetails = (payload: unknown) => action(StudentActionTypes.UPDATE_STUDENT_DETAILS, payload);

/* Checklist section - History */
export const saveHistory = (source: string, config: unknown) =>
  action(StudentActionTypes.SAVE_HISTORY, { source, config });
export const saveHistorySuccess = (cas: IVisa) => action(StudentActionTypes.SAVE_HISTORY_SUCCESS, cas);
export const saveHistoryFailure = (error: string) => action(StudentActionTypes.SAVE_HISTORY_FAILURE, error);
export const updateHistory = (payload: unknown) => action(StudentActionTypes.UPDATE_HISTORY, payload);

/* Checklist section - Consents */
export const saveConsents = (source: string, config: unknown) =>
  action(StudentActionTypes.SAVE_CONSENTS, { source, config });
export const saveConsentsSuccess = (cas: IVisa) => action(StudentActionTypes.SAVE_CONSENTS_SUCCESS, cas);
export const saveConsentsFailure = (error: string) => action(StudentActionTypes.SAVE_CONSENTS_FAILURE, error);

/* Checklist section - Finances */
export const saveDeposit = () => action(StudentActionTypes.SAVE_DEPOSIT);

export const createOrganisationSponsor = (data: OrganisationSponsorship) =>
  action(StudentActionTypes.CREATE_ORGANISATION_SPONSOR, {
    data,
  });
export const updateOrganisationSponsor = (key: string, data: OrganisationSponsorship) =>
  action(StudentActionTypes.UPDATE_ORGANISATION_SPONSOR, {
    key,
    data,
  });
export const removeOrganisationSponsor = (key: string) =>
  action(StudentActionTypes.REMOVE_ORGANISATION_SPONSOR, {
    key,
  });
export const removeOrganisationRegion = () => action(StudentActionTypes.REMOVE_ORGANISATION_REGION);

export const createScholarshipSponsor = (data: ScholarshipSponsorship) =>
  action(StudentActionTypes.CREATE_SCHOLARSHIP_SPONSOR, {
    data,
  });
export const updateScholarshipSponsor = (key: string, data: ScholarshipSponsorship) =>
  action(StudentActionTypes.UPDATE_SCHOLARSHIP_SPONSOR, {
    key,
    data,
  });
export const bulkUpdateScholarshipSponsor = (data: { key: string; data: ScholarshipSponsorship }[]) =>
  action(StudentActionTypes.BULK_UPDATE_SCHOLARSHIP_SPONSOR, {
    data,
  });
export const removeScholarshipSponsor = (key: string) =>
  action(StudentActionTypes.REMOVE_SCHOLARSHIP_SPONSOR, {
    key,
  });
export const removeScholarshipRegion = () => action(StudentActionTypes.REMOVE_SCHOLARSHIP_REGION);

export const createEducationSponsor = (data: EducationSponsorship) =>
  action(StudentActionTypes.CREATE_EDUCATION_SPONSOR, {
    data,
  });
export const updateEducationSponsor = (key: string, data: EducationSponsorship) =>
  action(StudentActionTypes.UPDATE_EDUCATION_SPONSOR, {
    key,
    data,
  });
export const removeEducationSponsor = (key: string) =>
  action(StudentActionTypes.REMOVE_EDUCATION_SPONSOR, {
    key,
  });
export const removeEducationRegion = () => action(StudentActionTypes.REMOVE_EDUCATION_REGION);

export const createFamilySponsor = (data: FamilySponsorship) =>
  action(StudentActionTypes.CREATE_FAMILY_SPONSOR, {
    data,
  });
export const updateFamilySponsor = (key: string, data: FamilySponsorship) =>
  action(StudentActionTypes.UPDATE_FAMILY_SPONSOR, {
    key,
    data,
  });
export const removeFamilySponsor = (key: string) =>
  action(StudentActionTypes.REMOVE_FAMILY_SPONSOR, {
    key,
  });
export const removeFamilyRegion = () => action(StudentActionTypes.REMOVE_FAMILY_REGION);

export const createOtherSponsor = (data: OtherSponsorship) =>
  action(StudentActionTypes.CREATE_OTHER_SPONSOR, {
    data,
  });
export const updateOtherSponsor = (key: string, data: OtherSponsorship) =>
  action(StudentActionTypes.UPDATE_OTHER_SPONSOR, {
    key,
    data,
  });
export const removeOtherSponsor = (key: string) =>
  action(StudentActionTypes.REMOVE_OTHER_SPONSOR, {
    key,
  });
export const removeOtherRegion = () => action(StudentActionTypes.REMOVE_OTHER_REGION);
export const saveFinanceInfoSuccess = (res: IVisa) => action(StudentActionTypes.SAVE_FINANCE_SUCCESS, res);
export const saveFinanceInfoFailure = (error: string) => action(StudentActionTypes.SAVE_FINANCE_FAILURE, error);
export const updateFinance = (payload: unknown) => action(StudentActionTypes.UPDATE_FINANCE, payload);

/* Interview */
export const savePreCasInterview = (source: string) => action(StudentActionTypes.SAVE_PRE_CAS_INTERVIEW, source);
export const savePreCasInterviewSuccess = (cas: IVisa) =>
  action(StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS, cas);
export const savePreCasInterviewFailure = (error: string) =>
  action(StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_FAILURE, error);

/* Visa Prep */
export const saveVisaPrepForm = (source: string) => action(StudentActionTypes.SAVE_VISA_PREP_FORM, source);
export const saveVisaPrepFormSuccess = (cas: IVisa) => action(StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS, cas);
export const saveVisaPrepFormFailure = (error: string) => action(StudentActionTypes.SAVE_VISA_PREP_FORM_FAILURE, error);

/* Documents */

export const saveDocuments = (
  sponsorType: string,
  sponsorKey: string,
  docType: string | null,
  docKey: string | null,
  uploadedBy: string,
) =>
  action(StudentActionTypes.SAVE_DOCUMENTS, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    uploadedBy,
  });
export const saveDocumentsSuccess = ({
  docs,
  complete,
  required,
}: {
  docs: IDocuments;
  complete: number;
  required: number;
}) => action(StudentActionTypes.SAVE_DOCUMENTS_SUCCESS, { docs, complete, required });
export const saveDocumentsFailure = (error: string) => action(StudentActionTypes.SAVE_DOCUMENTS_FAILURE, error);

export const deleteDocumentFile = (
  sponsorType: string,
  sponsorKey: string,
  docType: string | null,
  docKey: string | null,
  fileName: string,
  deletedBy: string,
) =>
  action(StudentActionTypes.DELETE_DOCUMENT_FILE, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    fileName,
    deletedBy,
  });

export const deleteDocumentFileFailure = (error: string) =>
  action(StudentActionTypes.DELETE_DOCUMENT_FILE_FAILURE, error);

export const deleteDocumentFileSuccess = (
  documents: IDocuments,
  fileName: string,
  complete: number,
  required: number,
) => action(StudentActionTypes.DELETE_DOCUMENT_FILE_SUCCESS, { documents, fileName, complete, required });

export const updateDocuments = (payload: unknown) => action(StudentActionTypes.UPDATE_DOCUMENTS, payload);

/* Finance Documents */
export const saveFinanceDocuments = (
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  uploadedBy: string,
) =>
  action(StudentActionTypes.SAVE_FINANCE_DOCUMENTS, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    uploadedBy,
  });
export const saveFinanceDocumentsSuccess = ({
  finDocs,
  complete,
  required,
}: {
  finDocs: IFinanceDocuments;
  complete: number;
  required: number;
}) => action(StudentActionTypes.SAVE_FINANCE_DOCUMENTS_SUCCESS, { finDocs, complete, required });

export const saveFinanceDocumentsFailure = (error: string) =>
  action(StudentActionTypes.SAVE_FINANCE_DOCUMENTS_FAILURE, error);

export const deleteFinanceFile = (
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  fileName: string,
  deletedBy: string,
) =>
  action(StudentActionTypes.DELETE_FINANCE_FILE, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    fileName,
    deletedBy,
  });

export const deleteFinanceFileSuccess = (
  financeDocuments: IFinanceDocuments,
  fileName: string,
  complete: number,
  required: number,
) => action(StudentActionTypes.DELETE_FINANCE_FILE_SUCCESS, { financeDocuments, fileName, complete, required });

export const deleteFinanceFileFailure = (error: string) =>
  action(StudentActionTypes.DELETE_FINANCE_FILE_FAILURE, error);

export const updateFinanceDocuments = (payload: unknown) =>
  action(StudentActionTypes.UPDATE_FINANCE_DOCUMENTS, payload);

/* Follow Up Documents */

export const saveFollowUpDocumentsShareCode = (docKey: string, data: Record<string, unknown>) =>
  action(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_SHARE_CODE, {
    data,
    docKey,
  });

export const saveFollowUpDocuments = (
  sponsorType: string,
  sponsorKey: string | null,
  docType: string,
  docKey: string,
  uploadedBy: string,
) =>
  action(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    uploadedBy,
  });

export const saveFollowUpDocumentsSuccess = ({
  followUpDocs,
  beforeComplete,
  beforeRequired,
  afterComplete,
  afterRequired,
}: {
  followUpDocs: IFollowUpDocuments;
  beforeComplete: number;
  beforeRequired: number;
  afterComplete: number;
  afterRequired: number;
}) =>
  action(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_SUCCESS, {
    followUpDocs,
    beforeComplete,
    beforeRequired,
    afterComplete,
    afterRequired,
  });
export const saveFollowUpDocumentsFailure = (error: string) =>
  action(StudentActionTypes.SAVE_FOLLOW_UP_DOCUMENTS_FAILURE, error);

export const deleteFollowUpFile = (
  sponsorType: string,
  sponsorKey: string | null,
  docType: string,
  docKey: string,
  fileName: string,
  deletedBy: string,
) =>
  action(StudentActionTypes.DELETE_FOLLOW_UP_FILE, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    fileName,
    deletedBy,
  });

export const deleteFollowUpFileSucess = (followUpDocuments: IFollowUpDocuments, fileName: string) =>
  action(StudentActionTypes.DELETE_FOLLOW_UP_FILE_SUCCESS, { followUpDocuments, fileName });

export const deleteFollowUpFileFailure = (error: string) =>
  action(StudentActionTypes.DELETE_FOLLOW_UP_FILE_FAILURE, error);

export const updateFollowUpDocuments = (payload: unknown) =>
  action(StudentActionTypes.UPDATE_FOLLOW_UP_DOCUMENTS, payload);

/* Messages */
export const saveMessage = (message: IMessage) => action(StudentActionTypes.SAVE_MESSAGE, message);
export const saveMessageSuccess = (message: IMessage) => action(StudentActionTypes.SAVE_MESSAGE_SUCCESS, message);
export const saveMessageFailure = (message: IMessage) => action(StudentActionTypes.SAVE_MESSAGE_FAILURE, message);
export const newMessage = (message: IMessage) => action(StudentActionTypes.NEW_MESSAGE, message);
export const markMessagesRead = (messageIds: Array<string>) =>
  action(StudentActionTypes.MARK_MESSAGES_READ, messageIds);
export const markMessagesReadSuccess = (messageIds: Array<string>, userRole: string) =>
  action(StudentActionTypes.MARK_MESSAGES_READ_SUCCESS, {
    messageIds,
    userRole,
  });
export const markMessagesReadFailure = (error: string) => action(StudentActionTypes.MARK_MESSAGES_READ_FAILURE, error);
export const markMessageUnread = (messageId: string) => action(StudentActionTypes.MARK_MESSAGE_UNREAD, messageId);
export const markMessageUnreadSuccess = (messageId: string, userRole: string) =>
  action(StudentActionTypes.MARK_MESSAGE_UNREAD_SUCCESS, {
    messageId,
    userRole,
  });
export const markMessageUnreadFailure = (error: string) =>
  action(StudentActionTypes.MARK_MESSAGE_UNREAD_FAILURE, error);

/* Student record */
export const getStudentRecord = (studentNumber: string, university: string, intakeId: string, offerId: string) => {
  return action(StudentActionTypes.GET_STUDENT_RECORD, {
    student_number: studentNumber,
    university,
    intakeId,
    offerId,
  });
};

export const getStudentDocs = () => {
  return action(StudentActionTypes.GET_STUDENT_DOCS);
};

export const getStudentDocsSuccess = (documents: unknown) => {
  return action(StudentActionTypes.GET_STUDENT_DOCS_SUCCESS, {
    documents,
  });
};

export const getStudentRecordedInterviews = () => {
  return action(StudentActionTypes.GET_STUDENT_RECORDED_INTERVIEWS);
};
export const getStudentRecordedInterviewsSuccess = (recordedInterviews: unknown) => {
  return action(StudentActionTypes.GET_STUDENT_RECORDED_INTERVIEWS_SUCCESS, {
    recordedInterviews,
  });
};
export const getStudentFinDocs = () => {
  return action(StudentActionTypes.GET_STUDENT_FIN_DOCS);
};
export const getStudentFinDocsSuccess = (finDocs: unknown) => {
  return action(StudentActionTypes.GET_STUDENT_FIN_DOCS_SUCCESS, {
    finDocs,
  });
};
export const getStudentMessages = () => {
  return action(StudentActionTypes.GET_STUDENT_MESSAGES);
};
export const getStudentFollowUpDocs = () => {
  return action(StudentActionTypes.GET_STUDENT_FOLLOW_UP_DOCS);
};
export const getStudentFollowUpDocsSuccess = (followUpDocs: unknown) => {
  return action(StudentActionTypes.GET_STUDENT_FOLLOW_UP_DOCS_SUCCESS, {
    followUpDocs,
  });
};
export const getStudentMessagesSuccess = (messages: unknown) => {
  return action(StudentActionTypes.GET_STUDENT_MESSAGES_SUCCESS, {
    messages,
  });
};
export const getStudentRecordSuccess = (student: IVisa, config: unknown, uniConfig: unknown) =>
  action(StudentActionTypes.GET_STUDENT_RECORD_SUCCESS, { student, config, uniConfig });
export const getStudentRecordFailure = (error: unknown) => action(StudentActionTypes.GET_STUDENT_RECORD_FAILURE, error);

// Action to register that the user is viewing a student (for WebSocket "being viewed by" feature)
// Used when returning to a student page with cached data
export const registerStudentView = () => action(StudentActionTypes.REGISTER_STUDENT_VIEW);

/* Helpers */
export const confirmSagaComplete = () => action(StudentActionTypes.SAGA_COMPLETE);

export const updateUniStudentNumber = (studentID: string, uniStudentNumber: string) =>
  action(StudentActionTypes.UPDATE_UNI_STUDENT_NUMBER, { studentID, uniStudentNumber });

export const updateOfferEvidence = (index: number, value: string) =>
  action(StudentActionTypes.UPDATE_OFFER_EVIDENCE, { index, value });
export const updateOfferEvidenceSuccess = (result: unknown) =>
  action(StudentActionTypes.UPDATE_OFFER_EVIDENCE_SUCCESS, result);
export const updateOfferEvidenceFailure = (error: string) =>
  action(StudentActionTypes.UPDATE_OFFER_EVIDENCE_FAILURE, error);

export const updateStatus = (studentID: string, status: Status) =>
  action(StudentActionTypes.UPDATE_STATUS, { studentID, status });
export const updateStatusSuccess = (result: unknown) => action(StudentActionTypes.UPDATE_STATUS_SUCCESS, result);

export const updateVisaStatus = (status: VisaStatus) => action(StudentActionTypes.UPDATE_VISA_STATUS, status);
export const updateVisaStatusSuccess = (result: unknown) =>
  action(StudentActionTypes.UPDATE_VISA_STATUS_SUCCESS, result);

export const overrideMaturityDate = (source: string) => action(StudentActionTypes.OVERRIDE_MATURED_DATE, source);

export const updateCore = (payload: unknown) => action(StudentActionTypes.UPDATE_CORE, payload);

export const downloadAttachments = (payload?: { includeHidden?: boolean }) =>
  action(StudentActionTypes.DOWNLOAD_ATTACHMENTS, payload);

export const downloadAttachmentsFailure = (err?: unknown) =>
  action(StudentActionTypes.DOWNLOAD_ATTACHMENTS_FAILURE, err);

export const downloadAttachmentsSuccess = (url: string) =>
  action(StudentActionTypes.DOWNLOAD_ATTACHMENTS_SUCCESS, { url });

export const updateConflict = ({ id, option, index }: { id: string; option: string; index?: number }) =>
  action(StudentActionTypes.UPDATE_CONFLICT, { id, option, index });

export const updateConflictSuccess = (res: IVisa) => action(StudentActionTypes.UPDATE_CONFLICT_SUCCESS, res);

export const updateConflictFailure = (error: string) => action(StudentActionTypes.UPDATE_CONFLICT_FAILURE, error);

/* Agent Section Approve */
export const approveAgentSection = () => action(StudentActionTypes.APPROVE_AGENT_SECTION);
export const approveAgentSectionFailure = (error: string) =>
  action(StudentActionTypes.APPROVE_AGENT_SECTION_FAILURE, error);

/* Agent Details Save */
export const saveAgentDetails = (fields: Record<string, string>) =>
  action(StudentActionTypes.SAVE_AGENT_DETAILS, fields);
export const saveAgentDetailsFailure = (error: string) => action(StudentActionTypes.SAVE_AGENT_DETAILS_FAILURE, error);

/* Student dashboard - Risks */
export const updateRisks = ({ source, clearedRisks }: { source: string; clearedRisks: unknown }) =>
  action(StudentActionTypes.UPDATE_RISKS_REQUEST, { source, clearedRisks });

export const updateRisksSuccess = (res: IRisk) => action(StudentActionTypes.UPDATE_RISKS_SUCCESS, res);

export const updateRisksFailure = (error: string) => action(StudentActionTypes.UPDATE_RISKS_FAILURE, error);

export const updateArrivalStatus = (arrivalStatus: IArrivalStatus) => {
  return action(EnrolmentStatusActionTypes.UPDATE_ARRIVAL_STATUS, arrivalStatus);
};

export const deleteStudent = () => action(StudentActionTypes.DELETE_STUDENT);
export const deleteStudentSuccess = (studentNumber: string) =>
  action(StudentActionTypes.DELETE_STUDENT_SUCCESS, studentNumber);
export const deleteStudentFailure = (error: string) => action(StudentActionTypes.DELETE_STUDENT_FAILURE, error);

export const makeActive = () => action(StudentActionTypes.MARK_ACTIVE);

export const removeImportHash = () => action(StudentActionTypes.REMOVE_IMPORT_HASH);
export const removeImportHashSuccess = () => action(StudentActionTypes.REMOVE_IMPORT_HASH_SUCCESS);
export const removeImportHashFailure = (error: string) => action(StudentActionTypes.REMOVE_IMPORT_HASH_FAILURE, error);

export const revokeStudentAccess = () => action(StudentActionTypes.REVOKE_STUDENT_ACCESS);

export const changeStudentNo = (newStudentNo: string) => action(StudentActionTypes.CHANGE_STUDENT_NO, newStudentNo);
export const changeStudentNoSuccess = (newStudentNo: string) =>
  action(StudentActionTypes.CHANGE_STUDENT_NO_SUCCESS, newStudentNo);
export const changeStudentNoFailure = (error: string) => action(StudentActionTypes.CHANGE_STUDENT_NO_FAILURE, error);

export const updateSummary = (summary: Partial<ISummary>) => action(StudentActionTypes.UPDATE_SUMMARY, { ...summary });

export const deleteFile = (
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  interviewType: string,
  eventType: string,
  eventKey: string,
  interviewHash: string,
  attemptHash: string,
  fileNames: string[],
  isGeneralDocumentModal: boolean,
  isFinanceDocumentModal: boolean,
  isFollowUpDocumentModal: boolean,
  isInterviewDocumentModal: boolean,
  isRecordedInterviewDocumentModal: boolean,
) =>
  action(StudentActionTypes.DELETE_FILE, {
    sponsorType,
    sponsorKey,
    docType,
    docKey,
    interviewType,
    eventType,
    eventKey,
    interviewHash,
    attemptHash,
    fileNames,
    isGeneralDocumentModal,
    isFinanceDocumentModal,
    isFollowUpDocumentModal,
    isInterviewDocumentModal,
    isRecordedInterviewDocumentModal,
  });

/* Checklist section - Scheduled Interviews */
export const saveInterviewDocuments = (interviewType: string, eventType: string, eventKey: string) =>
  action(StudentActionTypes.SAVE_INTERVIEW_DOCUMENTS, {
    interviewType,
    eventType,
    eventKey,
  });
export const saveInterviewDocumentsSuccess = (calendlyEvent: ICalendlyEvent | undefined) =>
  action(StudentActionTypes.SAVE_INTERVIEW_DOCUMENTS_SUCCESS, { calendlyEvent });
export const saveInterviewDocumentsFailure = (error: string) =>
  action(StudentActionTypes.SAVE_INTERVIEW_DOCUMENTS_FAILURE, error);

export const onRequestRecordedInterview = (interviewId: string, note: string) =>
  action(StudentActionTypes.REQUEST_RECORDED_INTERVIEW, { interviewId, note });
export const onRequestRecordedInterviewSuccess = (recordedInterviews: unknown) =>
  action(StudentActionTypes.REQUEST_RECORDED_INTERVIEW_SUCCESS, { recordedInterviews });
export const onRequestRecordedInterviewFailure = (error: string) =>
  action(StudentActionTypes.REQUEST_RECORDED_INTERVIEW_FAILURE, error);

export const requestScheduledInterview = (eventType: IEventType) =>
  action(StudentActionTypes.REQUEST_SCHEDULED_INTERVIEW, { eventType });
export const cancelScheduledInterview = (eventTypeId: string, eventKey: string, note: string) =>
  action(StudentActionTypes.CANCEL_SCHEDULED_INTERVIEW, { eventTypeId, eventKey, note });
export const cancelRecordedInterview = (interviewHash: string, note: string) =>
  action(StudentActionTypes.CANCEL_RECORDED_INTERVIEW, { interviewHash, note });
export const cancelRecordedInterviewSuccess = (recordedInterviews: unknown) =>
  action(StudentActionTypes.CANCEL_RECORDED_INTERVIEW_SUCCESS, { recordedInterviews });
export const cancelRecordedInterviewFailure = (error: string) =>
  action(StudentActionTypes.CANCEL_RECORDED_INTERVIEW_FAILURE, error);

export const failScheduledInterview = (
  eventTypeId: string,
  eventName: string,
  eventKey: string,
  note: string,
  rescind?: boolean,
) => action(StudentActionTypes.FAIL_SCHEDULED_INTERVIEW, { eventTypeId, eventName, eventKey, note, rescind });
export const resitScheduledInterview = (
  eventTypeId: string,
  eventName: string,
  eventKey: string,
  note: string,
  rescind?: boolean,
  newCalendlyInterview?: IEventType,
  sourceComponentId?: string,
) =>
  action(StudentActionTypes.RESIT_SCHEDULED_INTERVIEW, {
    eventTypeId,
    eventName,
    eventKey,
    note,
    rescind,
    newCalendlyInterview,
    sourceComponentId,
  });
export const passScheduledInterview = (
  eventTypeId: string,
  eventName: string,
  eventKey: string,
  status?: EventSystemStatus,
  sourceComponentId?: string,
) =>
  action(StudentActionTypes.PASS_SCHEDULED_INTERVIEW, { eventTypeId, eventName, eventKey, status, sourceComponentId });
export const toggleScheduledInterviewStatusSuccess = (
  calendlyEvent: ICalendlyEvent | undefined,
  eventType?: string,
  sourceComponentId?: string,
) => action(StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_SUCCESS, { calendlyEvent, eventType, sourceComponentId });
export const toggleScheduledInterviewStatusFailure = (error: string, eventType?: string, sourceComponentId?: string) =>
  action(StudentActionTypes.TOGGLE_SCHEDULED_INTERVIEW_FAILURE, { error, eventType, sourceComponentId });

export const deleteInterviewFile = (
  interviewType: string,
  eventType: string,
  eventKey: string,
  fileName: string,
  deletedBy: string,
) =>
  action(StudentActionTypes.DELETE_INTERVIEW_FILE, {
    interviewType,
    eventType,
    eventKey,
    fileName,
    deletedBy,
  });

export const deleteInterviewFileSuccess = (calendlyEvent: ICalendlyEvent | undefined, fileName: string) =>
  action(StudentActionTypes.DELETE_INTERVIEW_FILE_SUCCESS, { calendlyEvent, fileName });

export const deleteInterviewFileFailure = (error: string) =>
  action(StudentActionTypes.DELETE_INTERVIEW_FILE_FAILURE, error);

export const saveRecordedInterviewDocuments = (interviewHash: string, attemptHash: string) =>
  action(StudentActionTypes.SAVE_RECORDED_INTERVIEW_DOCUMENTS, {
    interviewHash,
    attemptHash,
  });
export const saveRecordedInterviewDocumentsSuccess = (offer: IOffer) =>
  action(StudentActionTypes.SAVE_RECORDED_INTERVIEW_DOCUMENTS_SUCCESS, { offer });
export const saveRecordedInterviewDocumentsFailure = (error: string) =>
  action(StudentActionTypes.SAVE_RECORDED_INTERVIEW_DOCUMENTS_FAILURE, error);
export const failRecordedInterview = (
  interviewHash: string,
  note: string,
  rescind?: boolean,
  sourceComponentId?: string,
) => action(StudentActionTypes.FAIL_RECORDED_INTERVIEW, { interviewHash, note, rescind, sourceComponentId });
export const passRecordedInterview = (
  interviewHash: string,
  status?: RecordedInterviewSystemStatus,
  sourceComponentId?: string,
) => action(StudentActionTypes.PASS_RECORDED_INTERVIEW, { interviewHash, status, sourceComponentId });
export const resitRecordedInterview = (
  interviewHash: string,
  note: string,
  rescind?: boolean,
  newRecordedInterviewId?: string,
  newInterviewName?: string,
  sourceComponentId?: string,
) =>
  action(StudentActionTypes.RESIT_RECORDED_INTERVIEW, {
    interviewHash,
    note,
    rescind,
    newRecordedInterviewId,
    newInterviewName,
    sourceComponentId,
  });

export const toggleRecordedInterviewStatusSuccess = ({
  interviewHash,
  status,
  note,
  undo,
  eventType,
  sourceComponentId,
}: {
  interviewHash: string;
  status: string;
  note?: string;
  eventType?: string;
  sourceComponentId?: string;
  undo?: boolean;
}) =>
  action(StudentActionTypes.TOGGLE_RECORDED_INTERVIEW_SUCCESS, {
    interviewHash,
    status,
    note,
    undo,
    eventType,
    sourceComponentId,
  });
export const toggleRecordedInterviewStatusFailure = (error: string, eventType?: string, sourceComponentId?: string) =>
  action(StudentActionTypes.TOGGLE_RECORDED_INTERVIEW_FAILURE, error, eventType, sourceComponentId);

export const deleteRecordedInterviewFile = (
  interviewType: string,
  interviewHash: string,
  attemptHash: string,
  fileName: string,
  deletedBy: string,
) =>
  action(StudentActionTypes.DELETE_RECORDED_INTERVIEW_FILE, {
    interviewType,
    interviewHash,
    attemptHash,
    fileName,
    deletedBy,
  });

export const deleteRecordedInterviewFileSuccess = (offer: IOffer, fileName: string) =>
  action(StudentActionTypes.DELETE_RECORDED_INTERVIEW_FILE_SUCCESS, { offer, fileName });

export const deleteRecordedInterviewFileFailure = (error: string) =>
  action(StudentActionTypes.DELETE_RECORDED_INTERVIEW_FILE_FAILURE, error);

export const updateRecordedInterviewWSDoc = (payload: unknown) =>
  action(StudentActionTypes.UPDATE_RECORDED_INTERVIEW_DOC, payload);
export const clearState = () => action(StudentActionTypes.CLEAR_STATE);

export const openStudent = () => action(StudentActionTypes.OPEN_STUDENT);

export const closeStudent = () => action(StudentActionTypes.CLOSE_STUDENT);

export const handleWebsocketStudentRecords = (data: unknown) =>
  action(StudentActionTypes.STUDENT_WEBSOCKET_CHANGE, { data });

export { getAuditLog, getAuditLogSuccess, getAuditLogFailure } from './auditLog/reducer';
